import { msg, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import {
  Alert,
  Box,
  Chip,
  Divider,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { StripedDataGrid } from '../tables/StripedDataGrid'
import { tryRefreshAccessToken } from '../../offline/auth-refresh'
import { getAccessTokenFromStorage } from '../../offline/session-store'
import {
  listCrashPackageExportCrashes,
  listCrashPackageExports,
} from '../../services/crash-package-exports'
import {
  mapPackageExportCrashRows,
  mapPackageExportGridRows,
  normalizePackageExportsListResponse,
  parsePackageExportCrashesResponse,
} from '../../services/normalize-package-exports-response'
import { formatDateForList } from '../../utils'

const DEFAULT_PAGE_SIZE = 25
const IMPORT_STATUS_IMPORTED = 'imported'
const IMPORT_STATUS_PENDING = 'pending_import'

function readPersistedSession() {
  try {
    const raw = localStorage.getItem('adams_user')
    const u = raw ? JSON.parse(raw) : null
    return {
      token: u?.token || localStorage.getItem('token') || null,
      init: u?.init || {},
    }
  } catch {
    return {
      token: localStorage.getItem('token') || null,
      init: {},
    }
  }
}

/**
 * @param {string|null|undefined} batchId
 */
function formatBatchIdDisplay(batchId) {
  if (!batchId) return '—'
  if (batchId.length <= 14) return batchId
  return `${batchId.slice(0, 8)}…${batchId.slice(-4)}`
}

/**
 * @param {Array<{ id: number, description?: string, short_description?: string, label?: string }>} organizations
 */
function buildOrganizationLookup(organizations) {
  const map = new Map()
  for (const org of organizations ?? []) {
    const id = Number(org.id)
    if (!Number.isFinite(id)) continue
    const label = org.description ?? org.label ?? org.short_description ?? String(id)
    map.set(id, String(label))
  }
  return map
}

/**
 * @param {Array<{ id: number, username?: string, name?: string, surname?: string }>} users
 */
function buildUserLookup(users) {
  const map = new Map()
  for (const user of users ?? []) {
    const id = Number(user.id)
    if (!Number.isFinite(id)) continue
    const parts = [user.name, user.surname, user.username].filter(Boolean)
    map.set(id, parts.length ? parts.join(' ') : String(id))
  }
  return map
}

/**
 * @typedef {Object} CrashesCacheEntry
 * @property {'loading'|'ready'|'error'} state
 * @property {ReturnType<typeof mapPackageExportCrashRows>} [rows]
 * @property {string} [error]
 */

/**
 * @param {object} props
 * @param {string} props.packageId
 * @param {string} props.bearerToken
 * @param {Map<number, string>} props.userLookup
 * @param {number | null} [props.organizationId]
 * @param {React.MutableRefObject<Map<string, CrashesCacheEntry>>} props.cacheRef
 * @param {() => void} props.onCacheUpdate
 */
function PackageExportCrashesDetail({
  packageId,
  bearerToken,
  userLookup,
  organizationId = null,
  cacheRef,
  onCacheUpdate,
}) {
  const { _ } = useLingui()
  const navigate = useNavigate()
  const theme = useTheme()
  const sectionBorder = theme.palette.newColors?.lightgray || theme.palette.divider
  const sectionBg =
    theme.palette.mode === 'light'
      ? theme.palette.newColors?.lightGray || '#f5f7fb'
      : theme.palette.background.default

  const cached = cacheRef.current.get(packageId)
  const [entry, setEntry] = useState(
    /** @type {CrashesCacheEntry} */ (cached ?? { state: 'loading' }),
  )

  useEffect(() => {
    const existing = cacheRef.current.get(packageId)
    if (existing && existing.state !== 'loading') {
      setEntry(existing)
      return
    }

    let cancelled = false
    const load = async () => {
      const loadingEntry = { state: /** @type {const} */ ('loading') }
      cacheRef.current.set(packageId, loadingEntry)
      setEntry(loadingEntry)

      let token = bearerToken
      try {
        let { status, body } = await listCrashPackageExportCrashes(packageId, token)
        if (status === 401) {
          const refreshed = await tryRefreshAccessToken()
          if (refreshed) {
            token = getAccessTokenFromStorage() || token
            const second = await listCrashPackageExportCrashes(packageId, token)
            status = second.status
            body = second.body
          }
        }

        if (cancelled) return

        if (status < 200 || status >= 300) {
          const message =
            typeof body === 'object' && body !== null && 'message' in body
              ? String(body.message)
              : _(msg`Failed to load imported crashes`) + ` (${status})`
          const errorEntry = { state: /** @type {const} */ ('error'), error: message }
          cacheRef.current.set(packageId, errorEntry)
          setEntry(errorEntry)
          onCacheUpdate()
          return
        }

        const parsed = parsePackageExportCrashesResponse(body)
        const readyEntry = {
          state: /** @type {const} */ ('ready'),
          rows: mapPackageExportCrashRows(parsed.items),
        }
        cacheRef.current.set(packageId, readyEntry)
        setEntry(readyEntry)
        onCacheUpdate()
      } catch (err) {
        if (cancelled) return
        const errorEntry = {
          state: /** @type {const} */ ('error'),
          error: err instanceof Error ? err.message : _(msg`Failed to load imported crashes.`),
        }
        cacheRef.current.set(packageId, errorEntry)
        setEntry(errorEntry)
        onCacheUpdate()
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [packageId, bearerToken, _, onCacheUpdate, cacheRef])

  const handleCrashRowClick = useCallback(
    (row) => {
      if (!row?.crashId) return
      const { token, init } = readPersistedSession()
      navigate('/home', {
        state: {
          token,
          init,
          openCrash: {
            id: String(row.crashId),
            organizationId: organizationId ?? null,
            reporterId: row.userId ?? null,
          },
        },
      })
    },
    [navigate, organizationId],
  )

  if (entry.state === 'loading') {
    return (
      <Box sx={{ p: 2 }}>
        <LinearProgress sx={{ mb: 1 }} />
        <Skeleton height={28} />
        <Skeleton height={28} />
        <Skeleton height={28} />
      </Box>
    )
  }

  if (entry.state === 'error') {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{entry.error}</Alert>
      </Box>
    )
  }

  const rows = entry.rows ?? []

  if (!rows.length) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          <Trans>No imported crashes in this package.</Trans>
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 2 }}>
      <TableContainer
        sx={{
          border: '1px solid',
          borderColor: sectionBorder,
          borderRadius: 1,
          backgroundColor: sectionBg,
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{_(msg`Crash ID`)}</TableCell>
              <TableCell>{_(msg`Report date`)}</TableCell>
              <TableCell>{_(msg`User`)}</TableCell>
              <TableCell>{_(msg`Imported at`)}</TableCell>
              <TableCell>{_(msg`Imported by`)}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => handleCrashRowClick(row)}
              >
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{row.crashId}</TableCell>
                <TableCell>{formatDateForList(row.reportDate)}</TableCell>
                <TableCell>
                  {row.userId != null
                    ? (userLookup.get(row.userId) ?? row.userId)
                    : '—'}
                </TableCell>
                <TableCell>{formatDateForList(row.importedAt)}</TableCell>
                <TableCell>
                  {row.importedByUserId != null
                    ? (userLookup.get(row.importedByUserId) ?? row.importedByUserId)
                    : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

/**
 * @param {object} props
 * @param {string} props.bearerToken
 * @param {Array<{ id: number, description?: string, short_description?: string, label?: string }>} [props.organizations]
 * @param {Array<{ id: number, username?: string, name?: string, surname?: string }>} [props.users]
 * @param {number} [props.organizationIdFilter]
 */
export default function CrashPackageImportHistoryPanel({
  bearerToken,
  organizations = [],
  users = [],
  organizationIdFilter,
}) {
  const { _ } = useLingui()
  const theme = useTheme()
  const muted = theme.palette.newColors?.gray || theme.palette.text.secondary
  const sectionBorder = theme.palette.newColors?.lightgray || theme.palette.divider

  const orgLookup = useMemo(() => buildOrganizationLookup(organizations), [organizations])
  const userLookup = useMemo(() => buildUserLookup(users), [users])

  const [rows, setRows] = useState([])
  const [rowCount, setRowCount] = useState(0)
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(/** @type {string|null} */ (null))
  const [selectedRowId, setSelectedRowId] = useState(/** @type {string|null} */ (null))
  const crashesCacheRef = useRef(/** @type {Map<string, CrashesCacheEntry>} */ (new Map()))
  const [, bumpCache] = useState(0)
  const onCacheUpdate = useCallback(() => bumpCache((n) => n + 1), [])

  const resolveHttpMessage = useCallback(
    (status, body) => {
      const fromBody =
        body && typeof body === 'object' && 'message' in body && typeof body.message === 'string'
          ? body.message
          : null
      if (fromBody) return fromBody
      switch (status) {
        case 401:
          return _(msg`Session expired. Please sign in again.`)
        case 403:
          return _(msg`You do not have permission to view import history.`)
        default:
          return _(msg`Failed to load import history`) + ` (${status})`
      }
    },
    [_],
  )

  const renderStatusChip = useCallback(
    (status) => {
      if (status === IMPORT_STATUS_IMPORTED) {
        return <Chip size="small" label={_(msg`Imported`)} color="success" variant="outlined" />
      }
      if (status === IMPORT_STATUS_PENDING) {
        return <Chip size="small" label={_(msg`Pending import`)} color="warning" variant="outlined" />
      }
      return (
        <Chip
          size="small"
          label={status || _(msg`Unknown`)}
          color="default"
          variant="outlined"
        />
      )
    },
    [_],
  )

  const columns = useMemo(
    () => [
      {
        field: 'packageId',
        headerName: _(msg`Package ID`),
        flex: 1.2,
        minWidth: 220,
        renderCell: (params) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: '100%',
              width: '100%',
            }}
          >
            <Tooltip title={params.value || ''}>
              <Typography
                variant="body2"
                noWrap
                sx={{
                  fontFamily: 'monospace',
                  fontSize: 12,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  width: '100%',
                }}
              >
                {params.value}
              </Typography>
            </Tooltip>
          </Box>
        ),
      },
      {
        field: 'date',
        headerName: _(msg`Date`),
        flex: 0.8,
        minWidth: 140,
        valueFormatter: (value) => formatDateForList(value),
      },
      {
        field: 'organizationId',
        headerName: _(msg`Organization`),
        flex: 0.7,
        minWidth: 120,
        valueGetter: (value, row) => {
          const id = row.organizationId
          if (id == null) return '—'
          return orgLookup.get(id) ?? id
        },
      },
      {
        field: 'userId',
        headerName: _(msg`User`),
        flex: 0.6,
        minWidth: 100,
        valueGetter: (value, row) => {
          const id = row.userId
          if (id == null) return '—'
          return userLookup.get(id) ?? id
        },
      },
      {
        field: 'records',
        headerName: _(msg`Records`),
        flex: 0.4,
        minWidth: 80,
        renderCell: (params) => {
          const count = params.row.records
          const display = count != null ? String(count) : '—'
          const summary = params.row.importSummary
          const isImported = params.row.status === IMPORT_STATUS_IMPORTED
          if (isImported && summary) {
            const tip = `${_(msg`Imported`)}: ${summary.imported ?? 0} / ${summary.files_total ?? 0}`
            return (
              <Tooltip title={tip}>
                <span>{display}</span>
              </Tooltip>
            )
          }
          return display
        },
      },
      {
        field: 'status',
        headerName: _(msg`Status`),
        flex: 0.55,
        minWidth: 130,
        renderCell: (params) => renderStatusChip(params.value),
      },
      {
        field: 'batchId',
        headerName: _(msg`Batch`),
        flex: 0.7,
        minWidth: 120,
        renderCell: (params) => {
          const batchId = params.value
          if (!batchId) return '—'
          const display = formatBatchIdDisplay(batchId)
          return (
            <Tooltip title={batchId}>
              <Typography
                component="span"
                variant="body2"
                sx={{ fontFamily: 'monospace', fontSize: 12 }}
              >
                {display}
              </Typography>
            </Tooltip>
          )
        },
      },
    ],
    [_, orgLookup, userLookup, renderStatusChip],
  )

  const loadList = useCallback(async () => {
    if (!bearerToken) {
      setError(_(msg`Sign in online to view import history.`))
      return
    }

    setLoading(true)
    setError(null)
    let token = bearerToken

    try {
      let { status, body } = await listCrashPackageExports(token, {
        organizationId: organizationIdFilter,
        limit: paginationModel.pageSize,
        offset: paginationModel.page * paginationModel.pageSize,
      })

      if (status === 401) {
        const refreshed = await tryRefreshAccessToken()
        if (refreshed) {
          token = getAccessTokenFromStorage() || token
          const second = await listCrashPackageExports(token, {
            organizationId: organizationIdFilter,
            limit: paginationModel.pageSize,
            offset: paginationModel.page * paginationModel.pageSize,
          })
          status = second.status
          body = second.body
        }
      }

      if (status === 401 || status === 403) {
        setError(resolveHttpMessage(status, body))
        setRows([])
        setRowCount(0)
        return
      }

      if (status < 200 || status >= 300) {
        setError(resolveHttpMessage(status, body))
        setRows([])
        setRowCount(0)
        return
      }

      const { items, total } = normalizePackageExportsListResponse(body)
      setRows(mapPackageExportGridRows(items))
      setRowCount(total)
    } catch (err) {
      setError(err instanceof Error ? err.message : _(msg`Failed to load import history.`))
      setRows([])
      setRowCount(0)
    } finally {
      setLoading(false)
    }
  }, [
    bearerToken,
    organizationIdFilter,
    paginationModel.page,
    paginationModel.pageSize,
    resolveHttpMessage,
    _,
  ])

  useEffect(() => {
    loadList()
  }, [loadList])

  const selectedRow = useMemo(
    () => rows.find((row) => row.id === selectedRowId) ?? null,
    [rows, selectedRowId],
  )

  useEffect(() => {
    if (selectedRowId && !rows.some((row) => row.id === selectedRowId)) {
      setSelectedRowId(null)
    }
  }, [rows, selectedRowId])

  const handleRowClick = useCallback((params) => {
    const id = String(params.id)
    setSelectedRowId((prev) => (prev === id ? null : id))
  }, [])

  return (
    <Paper
      sx={{
        width: '100%',
        marginTop: 2,
        p: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: sectionBorder,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: muted }}>
          <Trans>Import history</Trans>
        </Typography>
        {loading && <Chip size="small" color="info" label={_(msg`Loading…`)} />}
      </Stack>
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      <Divider sx={{ mb: 1.5 }} />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        <Trans>Select a row to view imported crashes for that package. Click a crash to open it.</Trans>
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ width: '100%', height: 420 }}>
        <StripedDataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          rowCount={rowCount}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
          onRowClick={handleRowClick}
          getRowClassName={(params) => {
            const stripe = params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
            return params.id === selectedRowId ? `${stripe} selected-row` : stripe
          }}
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            '& .MuiDataGrid-cell[data-field="packageId"]': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
            },
            '& .MuiDataGrid-row.selected-row': {
              boxShadow: `inset 4px 0 0 ${theme.palette.newColors?.yellow || '#E0D028'}`,
              backgroundColor: 'rgba(246, 213, 82, 0.35)',
            },
          }}
        />
      </Box>

      {selectedRow && (
        <Box
          sx={{
            mt: 2,
            border: '1px solid',
            borderColor: sectionBorder,
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1,
              bgcolor: theme.palette.action.hover,
              borderBottom: '1px solid',
              borderColor: sectionBorder,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              <Trans>Package details</Trans>
              {' — '}
              <Typography
                component="span"
                variant="subtitle2"
                sx={{ fontFamily: 'monospace', fontWeight: 500 }}
              >
                {selectedRow.packageId}
              </Typography>
            </Typography>
          </Box>

          {selectedRow.status !== IMPORT_STATUS_IMPORTED ? (
            <Box sx={{ p: 2 }}>
              <Alert severity="info">
                <Trans>Package not imported yet. No imported crashes to show.</Trans>
              </Alert>
            </Box>
          ) : (
            <PackageExportCrashesDetail
              packageId={String(selectedRow.packageId)}
              bearerToken={bearerToken}
              userLookup={userLookup}
              organizationId={selectedRow.organizationId}
              cacheRef={crashesCacheRef}
              onCacheUpdate={onCacheUpdate}
            />
          )}
        </Box>
      )}
    </Paper>
  )
}
